'use client';

import { useEffect, useState } from 'react';

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

const DISMISSED_KEY = 'garden-install-dismissed';

function isIOS(): boolean {
  if (typeof navigator === 'undefined') return false;
  return (
    /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
  );
}

function isInStandaloneMode(): boolean {
  if (typeof window === 'undefined') return false;
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    (window.navigator as { standalone?: boolean }).standalone === true
  );
}

export default function InstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [mode, setMode] = useState<'hidden' | 'android' | 'ios'>('hidden');

  useEffect(() => {
    if (isInStandaloneMode() || localStorage.getItem(DISMISSED_KEY)) return;

    if (isIOS()) {
      // Small delay so it doesn't flash immediately on first page load
      const t = setTimeout(() => setMode('ios'), 3000);
      return () => clearTimeout(t);
    }

    function handleBeforeInstall(e: Event) {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
      setMode('android');
    }
    window.addEventListener('beforeinstallprompt', handleBeforeInstall);
    return () => window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
  }, []);

  function dismiss() {
    localStorage.setItem(DISMISSED_KEY, '1');
    setMode('hidden');
  }

  async function install() {
    if (!deferredPrompt) return;
    await deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === 'dismissed') localStorage.setItem(DISMISSED_KEY, '1');
    setDeferredPrompt(null);
    setMode('hidden');
  }

  if (mode === 'hidden') return null;

  return (
    <div
      className="fixed bottom-[76px] md:bottom-4 left-4 right-4 md:left-auto md:right-4 md:max-w-sm z-[60] bg-white border border-sand rounded-2xl shadow-lg p-4 flex items-start gap-3"
      role="banner"
    >
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img src="/icons/icon-192.png" alt="" className="w-10 h-10 rounded-xl flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="font-body text-[14px] font-semibold text-dark-text leading-snug">
          Install the Garden app
        </p>

        {mode === 'ios' ? (
          <>
            <p className="font-body text-[12px] text-sage mt-0.5 leading-snug">
              Tap{' '}
              <span className="inline-flex items-center gap-0.5 align-middle">
                <ShareIcon />
              </span>
              {' '}then <strong className="text-dark-text">Add to Home Screen</strong> to install.
            </p>
            <div className="flex gap-2 mt-3">
              <button
                onClick={dismiss}
                className="flex-1 bg-[#f5f5f0] text-sage font-body text-[13px] py-2 rounded-lg hover:bg-[#eeede8] transition-colors"
              >
                Dismiss
              </button>
            </div>
          </>
        ) : (
          <>
            <p className="font-body text-[12px] text-sage mt-0.5 leading-snug">
              Add to your home screen for a better experience.
            </p>
            <div className="flex gap-2 mt-3">
              <button
                onClick={install}
                className="flex-1 bg-deep-green text-white font-body text-[13px] font-semibold py-2 rounded-lg hover:opacity-90 transition-opacity"
              >
                Install app
              </button>
              <button
                onClick={dismiss}
                className="flex-1 bg-[#f5f5f0] text-sage font-body text-[13px] py-2 rounded-lg hover:bg-[#eeede8] transition-colors"
              >
                Not now
              </button>
            </div>
          </>
        )}
      </div>

      {/* Close button */}
      <button
        onClick={dismiss}
        className="flex-shrink-0 text-sage hover:text-dark-text transition-colors mt-0.5"
        aria-label="Dismiss"
      >
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
          <path d="M4 4l8 8M12 4l-8 8" />
        </svg>
      </button>
    </div>
  );
}

function ShareIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'inline' }}>
      <path d="M7 1v8M4 4l3-3 3 3" />
      <path d="M2 8v4h10V8" />
    </svg>
  );
}
